import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import {
  ArrowLeft, Send, Users, Mail, MessageSquare, Bell, BellRing, Smartphone, History,
  Loader2, Eye, CheckCircle2, AlertTriangle, Clock, DollarSign, Paperclip, X,
} from 'lucide-react';
import UserTypeahead from '../../components/UserTypeahead';
import { formatDateMMDDYYYY } from '../../utils/timeFormat';

const CHANNELS = [
  { id: 'email', label: 'Email', icon: Mail, hint: 'via SendGrid, respects unsubscribe' },
  { id: 'in_app', label: 'In-app', icon: Bell, hint: 'notification center' },
  { id: 'sms', label: 'SMS', icon: MessageSquare, hint: 'opted-in talent only' },
  { id: 'push', label: 'Push', icon: Smartphone, hint: 'push-enabled talent' },
];

const SCOPES = [
  { value: 'all', label: 'All active talent' },
  { value: 'filtered', label: 'By market / state / role' },
  { value: 'specific', label: 'Specific talent' },
];

export default function TalentCommunications() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('compose');

  // audience
  const [scope, setScope] = useState('all');
  const [markets, setMarkets] = useState([]);
  const [selMarkets, setSelMarkets] = useState([]);
  const [states, setStates] = useState('');
  const [roles, setRoles] = useState('');
  const [cities, setCities] = useState('');
  const [specificTalent, setSpecificTalent] = useState([]);

  // channels + message
  const [channels, setChannels] = useState(['email', 'in_app']);
  const [title, setTitle] = useState('');
  const [subject, setSubject] = useState('');
  const [bodyText, setBodyText] = useState('');

  // templates
  const [templates, setTemplates] = useState([]);

  // attachments (email channel)
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);

  // preview / schedule
  const [counts, setCounts] = useState(null);
  const [countLoading, setCountLoading] = useState(false);
  const [smsCost, setSmsCost] = useState(null);
  const [scheduleAt, setScheduleAt] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sending, setSending] = useState(false);

  // history
  const [history, setHistory] = useState([]);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    api.get('/admin/talent-broadcasts/markets').then(r => setMarkets(r.data || [])).catch(() => {});
    api.get('/admin/broadcast-templates').then(r => setTemplates(r.data?.templates || [])).catch(() => {});
  }, []);

  const audience = useMemo(() => {
    const a = { scope };
    if (scope === 'specific') {
      a.talent_ids = specificTalent.map(u => u.id);
    } else if (scope === 'filtered') {
      if (selMarkets.length) a.market_ids = selMarkets;
      const st = states.split(',').map(s => s.trim()).filter(Boolean);
      const rl = roles.split(',').map(s => s.trim()).filter(Boolean);
      const ct = cities.split(',').map(s => s.trim()).filter(Boolean);
      if (st.length) a.states = st;
      if (rl.length) a.roles = rl;
      if (ct.length) a.cities = ct;
    }
    return a;
  }, [scope, specificTalent, selMarkets, states, roles, cities]);

  const audienceValid = useMemo(() => {
    if (scope === 'all') return true;
    if (scope === 'specific') return specificTalent.length > 0;
    if (scope === 'filtered') return !!(selMarkets.length || states.trim() || roles.trim() || cities.trim());
    return false;
  }, [scope, specificTalent, selMarkets, states, roles, cities]);

  const refreshCounts = useCallback(async () => {
    setCountLoading(true);
    try {
      const { data } = await api.post('/admin/talent-broadcasts/preview', { audience });
      setCounts(data);
      if (channels.includes('sms')) {
        const sc = await api.post('/admin/talent-broadcasts/estimate-sms', { audience, message: bodyText });
        setSmsCost(sc.data);
      } else {
        setSmsCost(null);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not compute recipient count');
    } finally {
      setCountLoading(false);
    }
  }, [audience, channels, bodyText]);

  // Auto recompute counts when audience/channels change (debounced).
  useEffect(() => {
    const t = setTimeout(refreshCounts, 500);
    return () => clearTimeout(t);
  }, [refreshCounts]);

  const fetchHistory = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/talent-broadcasts?limit=100');
      setHistory(Array.isArray(data) ? data : []);
    } catch { setHistory([]); }
  }, []);
  useEffect(() => { if (tab === 'history') fetchHistory(); }, [tab, fetchHistory]);

  const toggleChannel = (id) => {
    setChannels(prev => prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]);
  };

  const toggleMarket = (id) => {
    setSelMarkets(prev => prev.includes(id) ? prev.filter(m => m !== id) : [...prev, id]);
  };

  const applyTemplate = (tplId) => {
    const t = templates.find(x => x.id === tplId);
    if (!t) return;
    if (t.subject) setSubject(t.subject);
    if (t.body_text) setBodyText(t.body_text);
    else if (t.sms_body) setBodyText(t.sms_body);
    if (!title) setTitle(t.name);
    toast.message(`Applied template: ${t.name}`);
  };

  const canSend = !!(title.trim() && bodyText.trim() && channels.length > 0 && audienceValid && !countLoading);

  const resetForm = () => {
    setTitle(''); setSubject(''); setBodyText('');
    setScheduleAt(''); setSpecificTalent([]); setSelMarkets([]);
    setStates(''); setRoles(''); setCities(''); setAttachments([]);
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        if (f.size > 5 * 1024 * 1024) { toast.error(`${f.name} is over 5MB`); continue; }
        const fd = new FormData();
        fd.append('file', f);
        const { data } = await api.post('/admin/talent-broadcasts/attachments', fd,
          { headers: { 'Content-Type': 'multipart/form-data' } });
        setAttachments(prev => [...prev, data]);
      }
      toast.success('Attachment added');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const removeAttachment = (path) => setAttachments(prev => prev.filter(a => a.path !== path));

  const doSubmit = async (sendNow) => {
    setSending(true);
    try {
      const payload = {
        title: title.trim(),
        subject: subject || undefined,
        body_text: bodyText,
        channels,
        audience,
        send_now: sendNow,
        scheduled_for: !sendNow && scheduleAt ? new Date(scheduleAt).toISOString() : undefined,
        attachments: attachments.length ? attachments : undefined,
      };
      await api.post('/admin/talent-broadcasts', payload);
      toast.success(sendNow ? 'Broadcast is sending' : (scheduleAt ? 'Broadcast scheduled' : 'Draft saved'));
      setConfirmOpen(false);
      resetForm();
      setTab('history');
      fetchHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create broadcast');
    } finally {
      setSending(false);
    }
  };

  const openDetail = async (id) => {
    try {
      const { data } = await api.get(`/admin/talent-broadcasts/${id}`);
      setDetail(data);
    } catch (e) { toast.error('Failed to load detail'); }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-5xl mx-auto px-4 py-6">
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin/users')} data-testid="tc-back" className="mb-3">
          <ArrowLeft className="w-4 h-4 mr-1" /> User Management
        </Button>

        <h1 className="text-2xl font-semibold mb-1 flex items-center gap-2">
          <Send className="w-6 h-6 text-cyan-600" /> Talent Communications
        </h1>
        <p className="text-sm text-slate-500 mb-5">
          Send memos, announcements and updates to active talent. Suspended talent are always excluded.
        </p>

        <Card className="mb-5 border-cyan-200 bg-cyan-50/40">
          <CardContent className="py-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <BellRing className="w-5 h-5 text-cyan-600 shrink-0" />
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-800">Smart Reminder Emails</div>
                <div className="text-xs text-slate-500 truncate">Automatic daily nudges for talent who haven’t finished their profile.</div>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={() => navigate('/admin/profile-nudges')}
              data-testid="tc-open-smart-reminders" className="shrink-0 border-cyan-300 text-cyan-700">
              Open
            </Button>
          </CardContent>
        </Card>

        <div className="flex gap-2 mb-5 border-b">
          <button onClick={() => setTab('compose')} data-testid="tc-tab-compose"
            className={`px-4 py-2 text-sm font-medium border-b-2 transition ${tab === 'compose' ? 'border-cyan-600 text-cyan-700' : 'border-transparent text-slate-500'}`}>
            Compose
          </button>
          <button onClick={() => setTab('history')} data-testid="tc-tab-history"
            className={`px-4 py-2 text-sm font-medium border-b-2 transition ${tab === 'history' ? 'border-cyan-600 text-cyan-700' : 'border-transparent text-slate-500'}`}>
            <History className="inline w-4 h-4 mr-1" /> History
          </button>
        </div>

        {tab === 'compose' && (
          <div className="grid gap-4">
            {/* 1. Audience */}
            <Card>
              <CardHeader><CardTitle className="text-base">1. Audience</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <Select value={scope} onValueChange={(v) => { setScope(v); setCounts(null); }}>
                  <SelectTrigger data-testid="tc-scope"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SCOPES.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>

                {scope === 'specific' && (
                  <UserTypeahead value={specificTalent} onChange={setSpecificTalent} roles="talent"
                    placeholder="Search talent by name, email or phone…" data-testid="tc-talent-picker" />
                )}

                {scope === 'filtered' && (
                  <div className="space-y-3">
                    <div>
                      <label className="text-xs font-medium text-slate-600 mb-1 block">Markets</label>
                      <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto p-1 border rounded-md" data-testid="tc-markets">
                        {markets.map(m => (
                          <button key={m.id} type="button" onClick={() => toggleMarket(m.id)}
                            data-testid={`tc-market-${m.slug}`}
                            className={`text-xs px-2 py-1 rounded-full border transition ${selMarkets.includes(m.id) ? 'bg-cyan-600 text-white border-cyan-600' : 'bg-white text-slate-600 border-slate-300'}`}>
                            {m.name}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="grid md:grid-cols-3 gap-2">
                      <div>
                        <label className="text-xs text-slate-500">States (comma)</label>
                        <Input value={states} onChange={e => setStates(e.target.value)} placeholder="GA, FL" data-testid="tc-states" />
                      </div>
                      <div>
                        <label className="text-xs text-slate-500">Roles (comma)</label>
                        <Input value={roles} onChange={e => setRoles(e.target.value)} placeholder="Brand Ambassador" data-testid="tc-roles" />
                      </div>
                      <div>
                        <label className="text-xs text-slate-500">Cities (comma)</label>
                        <Input value={cities} onChange={e => setCities(e.target.value)} placeholder="Atlanta" data-testid="tc-cities" />
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-2 pt-1" data-testid="tc-recipient-count">
                  <Users className="w-4 h-4 text-cyan-600" />
                  {countLoading ? (
                    <span className="text-sm text-slate-400 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> counting…</span>
                  ) : counts ? (
                    <span className="text-sm text-slate-700">
                      <strong>{counts.audience_total.toLocaleString()}</strong> eligible active talent
                    </span>
                  ) : <span className="text-sm text-slate-400">—</span>}
                </div>
              </CardContent>
            </Card>

            {/* 2. Channels */}
            <Card>
              <CardHeader><CardTitle className="text-base">2. Channels</CardTitle></CardHeader>
              <CardContent>
                <div className="grid sm:grid-cols-2 gap-2">
                  {CHANNELS.map(ch => {
                    const Icon = ch.icon;
                    const active = channels.includes(ch.id);
                    const n = counts?.per_channel?.[ch.id];
                    return (
                      <button key={ch.id} type="button" onClick={() => toggleChannel(ch.id)}
                        data-testid={`tc-channel-${ch.id}`}
                        className={`flex items-start gap-2 p-3 rounded-lg border text-left transition ${active ? 'border-cyan-500 bg-cyan-50' : 'border-slate-200 bg-white'}`}>
                        <Checkbox checked={active} className="mt-0.5" />
                        <div className="flex-1">
                          <div className="flex items-center gap-1.5 text-sm font-medium text-slate-800">
                            <Icon className="w-4 h-4" /> {ch.label}
                            {active && typeof n === 'number' && (
                              <Badge variant="secondary" className="ml-1">{n.toLocaleString()}</Badge>
                            )}
                          </div>
                          <div className="text-[11px] text-slate-400">{ch.hint}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
                {channels.includes('sms') && smsCost && (
                  <div className="mt-3 flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="tc-sms-cost">
                    <DollarSign className="w-4 h-4" />
                    Est. SMS cost: <strong>${smsCost.estimated_cost_usd}</strong>
                    <span className="text-xs text-amber-600">({smsCost.recipients.toLocaleString()} recipients × {smsCost.segments_per_message} segment)</span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* 3. Message */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-base">3. Message</CardTitle>
                {templates.length > 0 && (
                  <Select onValueChange={applyTemplate}>
                    <SelectTrigger className="w-56 h-8 text-xs" data-testid="tc-template-select"><SelectValue placeholder="Apply template…" /></SelectTrigger>
                    <SelectContent>
                      {templates.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <label className="text-xs text-slate-500">Internal title</label>
                  <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. October policy memo" data-testid="tc-title" />
                </div>
                <div>
                  <label className="text-xs text-slate-500">Subject (email / in-app title)</label>
                  <Input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Subject line" data-testid="tc-subject" />
                </div>
                <div>
                  <label className="text-xs text-slate-500">Message — use {'{{first_name}}'} to personalize</label>
                  <Textarea rows={7} value={bodyText} onChange={e => setBodyText(e.target.value)}
                    placeholder="Hi {{first_name}}, …" data-testid="tc-body" />
                </div>
                <div>
                  <label className="text-xs text-slate-500 flex items-center gap-1">
                    <Paperclip className="w-3 h-3" /> Attachments <span className="text-slate-400">(email only, ≤5MB each)</span>
                  </label>
                  <div className="flex flex-wrap items-center gap-2 mt-1">
                    {attachments.map(a => (
                      <span key={a.path} className="inline-flex items-center gap-1 text-xs bg-slate-100 border border-slate-200 rounded-full pl-2 pr-1 py-1" data-testid={`tc-attachment-${a.filename}`}>
                        <Paperclip className="w-3 h-3 text-slate-500" />
                        <span className="max-w-[160px] truncate">{a.filename}</span>
                        <span className="text-slate-400">({Math.round(a.size / 1024)}KB)</span>
                        <button type="button" onClick={() => removeAttachment(a.path)} className="hover:bg-slate-200 rounded-full p-0.5" data-testid={`tc-attachment-remove-${a.filename}`}>
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                    <label className="inline-flex items-center gap-1 text-xs text-cyan-700 border border-dashed border-cyan-300 rounded-full px-2.5 py-1 cursor-pointer hover:bg-cyan-50" data-testid="tc-attachment-add">
                      {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Paperclip className="w-3 h-3" />}
                      {uploading ? 'Uploading…' : 'Add file'}
                      <input type="file" className="hidden" multiple onChange={handleUpload} disabled={uploading} data-testid="tc-attachment-input" />
                    </label>
                  </div>
                  {attachments.length > 0 && !channels.includes('email') && (
                    <p className="text-[11px] text-amber-600 mt-1">Attachments are only delivered on the Email channel.</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* 4. Schedule + send */}
            <Card>
              <CardHeader><CardTitle className="text-base">4. Schedule &amp; send</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <label className="text-xs text-slate-500 flex items-center gap-1"><Clock className="w-3 h-3" /> Schedule (optional)</label>
                  <Input type="datetime-local" value={scheduleAt} onChange={e => setScheduleAt(e.target.value)} className="w-64" data-testid="tc-schedule" />
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" disabled={!canSend || sending} onClick={() => doSubmit(false)} data-testid="tc-save-draft">
                    {scheduleAt ? 'Schedule' : 'Save draft'}
                  </Button>
                  <Button disabled={!canSend || sending} onClick={() => setConfirmOpen(true)} data-testid="tc-send-now"
                    className="bg-cyan-600 hover:bg-cyan-700">
                    <Send className="w-4 h-4 mr-1" /> Send now
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {tab === 'history' && (
          <div className="grid gap-2" data-testid="tc-history">
            {history.length === 0 && <p className="text-sm text-slate-400">No broadcasts yet.</p>}
            {history.map(b => (
              <Card key={b.id} className="cursor-pointer hover:border-cyan-300" onClick={() => openDetail(b.id)} data-testid={`tc-history-${b.id}`}>
                <CardContent className="py-3 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-800 truncate">{b.title}</div>
                    <div className="text-xs text-slate-400">
                      {(b.channels || []).join(', ')} · {b.audience_count?.toLocaleString?.() || 0} audience · {b.created_at ? formatDateMMDDYYYY(b.created_at) : ''}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {typeof b.sent_count === 'number' && <Badge variant="secondary">{b.sent_count} sent</Badge>}
                    {b.skipped_count > 0 && <Badge variant="outline" className="text-amber-700 border-amber-300">{b.skipped_count} skipped</Badge>}
                    <Badge className={b.status === 'sent' ? 'bg-green-600' : b.status === 'scheduled' ? 'bg-blue-600' : b.status === 'sending' ? 'bg-cyan-600' : 'bg-slate-400'}>{b.status}</Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Confirm send dialog */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="tc-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Eye className="w-5 h-5" /> Confirm broadcast</DialogTitle>
            <DialogDescription>Review before sending. This cannot be undone.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 text-sm">
            <div><span className="text-slate-500">Audience:</span> <strong>{counts?.audience_total?.toLocaleString() || 0}</strong> active talent</div>
            <div><span className="text-slate-500">Channels:</span> {channels.map(c => {
              const n = counts?.per_channel?.[c];
              return <Badge key={c} variant="secondary" className="mr-1">{c}{typeof n === 'number' ? ` · ${n.toLocaleString()}` : ''}</Badge>;
            })}</div>
            {channels.includes('sms') && smsCost && (
              <div className="text-amber-700 flex items-center gap-1"><DollarSign className="w-4 h-4" /> Est. SMS cost ${smsCost.estimated_cost_usd}</div>
            )}
            <div className="bg-slate-50 border rounded p-2 mt-2">
              <div className="font-medium text-slate-700">{subject || title}</div>
              <div className="text-slate-600 whitespace-pre-wrap text-xs mt-1">{bodyText.slice(0, 280)}{bodyText.length > 280 ? '…' : ''}</div>
            </div>
            <div className="flex items-start gap-1 text-xs text-slate-400 pt-1">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5" /> Suspended, unsubscribed and opted-out talent are automatically excluded at send.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} data-testid="tc-confirm-cancel">Cancel</Button>
            <Button disabled={sending || countLoading || !audienceValid} onClick={() => doSubmit(true)} className="bg-cyan-600 hover:bg-cyan-700" data-testid="tc-confirm-send">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1" /> Send now</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent data-testid="tc-detail-dialog" className="max-w-lg">
          <DialogHeader><DialogTitle>{detail?.title}</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-3 text-sm">
              <div className="flex items-center gap-2">
                <Badge className={detail.status === 'sent' ? 'bg-green-600' : 'bg-slate-400'}>{detail.status}</Badge>
                <span className="text-slate-500">{detail.audience_count?.toLocaleString()} audience</span>
              </div>
              <div className="bg-slate-50 border rounded p-2">
                <div className="font-medium">{detail.subject}</div>
                <div className="text-xs text-slate-600 whitespace-pre-wrap mt-1">{detail.body_text}</div>
              </div>
              <div>
                <div className="text-xs font-medium text-slate-500 mb-1">Delivery by channel</div>
                {detail.delivery?.by_channel && Object.keys(detail.delivery.by_channel).length > 0 ? (
                  Object.entries(detail.delivery.by_channel).map(([ch, st]) => (
                    <div key={ch} className="flex items-center gap-2 text-xs py-0.5">
                      <span className="w-16 font-medium">{ch}</span>
                      {Object.entries(st).map(([status, n]) => (
                        <Badge key={status} variant="outline" className={status === 'sent' ? 'text-green-700 border-green-300' : status === 'skipped' ? 'text-amber-700 border-amber-300' : 'text-slate-600'}>
                          {status}: {n}
                        </Badge>
                      ))}
                    </div>
                  ))
                ) : <span className="text-xs text-slate-400">No delivery records yet.</span>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
